/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */

import {
  EventEmitter,
  Inject,
  Injectable,
  NgZone,
  Type,
  ɵstringify as stringify,
  forwardRef,
} from "@angular/core";
import { MessageBus } from "./message_bus";
import { Serializer, SerializerTypes } from "./serializer";

import { REGISTRY_CHANNEL, ON_WEB_WORKER, DEBUG } from "./api";
import { ServiceMessageBroker } from "./service_message_broker";
import { ServiceMessageBrokerRegistry } from "./service_message_broker_registry";
import { PostMessageBus, PostMessageBusSink, PostMessageBusSource } from "./post_message_bus";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

@Injectable()
export class ClientMessageBrokerFactory {
  /** @internal */
  private _messageBus: MessageBus;
  private _serializer: Serializer;
  private isDebug;
  private _registry: ServiceMessageBrokerRegistry;
  private _ngZone: NgZone;

  constructor(
    _messageBus: MessageBus,
    _serializer: Serializer,
    @Inject(DEBUG) isDebug,
    _registry: ServiceMessageBrokerRegistry,
    _ngZone: NgZone,
  ) {
    this._messageBus = _messageBus;
    this.isDebug = isDebug;
    this._registry = _registry;
    this._serializer = _serializer;
    this._ngZone = _ngZone;
  }

  /**
   * Initializes the given channel and attaches a new {@link ClientMessageBroker} to it.
   */
  createMessageBroker(
    channel: string,
    runInZone: boolean = true,
    hasTransfers?: boolean,
  ): ClientMessageBroker {
    let serviceChannel = new MessageChannel();
    let clientPort = serviceChannel.port1;
    let servicePort = serviceChannel.port2;

    let sink = new PostMessageBusSink(clientPort);
    let source = new PostMessageBusSource(clientPort);
    let messageBus = new PostMessageBus(sink, source);
    messageBus.attachToZone(this._ngZone);
    messageBus.initChannel(channel, runInZone, hasTransfers);

    let clientBroker = new ClientMessageBroker_(
      messageBus,
      this._serializer,
      channel,
      hasTransfers,
    );
    clientBroker.isDebug = this.isDebug;

    this._registry.registerClientMessageBroker(channel, servicePort);
    clientPort.start();

    if (this.isDebug) {
      logger.out.info(`Added client channel ${channel} to registry. started port & sink`);
    }

    return clientBroker;
  }
}

/**
 * @experimental WebWorker support in Angular is experimental.
 */
export abstract class ClientMessageBroker {
  abstract runOnService(args: UiArguments, returnType: any): Promise<any> | null;
}

interface PromiseCompleter {
  resolve: (result: any) => void;
  reject: (err: any) => void;
}

export class ClientMessageBroker_ extends ClientMessageBroker {
  private _pending = new Map<string, PromiseCompleter>();
  private _sink: EventEmitter<any>;
  private _pendingTimeoutId: Map<string, any> = new Map<string, any>();
  private _hasTransfers: boolean;

  /** @internal */
  public _serializer: Serializer;
  public isDebug: boolean = false;

  constructor(
    messageBus: MessageBus,
    _serializer: Serializer,
    public channel: any,
    _hasTransfers = false,
  ) {
    super();
    this._sink = messageBus.to(channel);
    this._serializer = _serializer;
    this._hasTransfers = _hasTransfers;
    const source = messageBus.from(channel);

    source.subscribe({
      next: (message: ResponseMessageData) => this._handleMessage(message),
    });
  }

  private _generateMessageId(name: string): string {
    const time: string = stringify(new Date().getTime());
    let iteration: number = 0;
    let id: string = name + time + stringify(iteration);
    while (this._pending.has(id)) {
      id = `${name}${time}${iteration}`;
      iteration++;
    }
    return id;
  }

  runOnService(args: UiArguments, returnType: any): Promise<any> | null {
    const fnArgs: any[] = [];
    const transfer: Array<ArrayBuffer | Blob | MessagePort> = [];

    args.args.forEach((argument) => {
      if (argument.type != null) {
        fnArgs.push(this._serializer.serialize(argument.value, argument.type));

        if (argument.isTransferArg && !this._hasTransfers) {
          throw new Error("Transfers are not enabled for channel");
        }
        if (
          (argument.isTransferArg && this._hasTransfers && argument.value instanceof ArrayBuffer) ||
          argument.value instanceof Blob ||
          argument.value instanceof MessagePort
        ) {
          transfer.push(argument.value);
        }
      }
    });

    let promise: Promise<any>;
    let id: string = null;
    if (returnType != null) {
      const RPC_TIMEOUT: number = 120000;
      let completer: PromiseCompleter;
      promise = new Promise((resolve, reject) => {
        completer = { resolve, reject };
      });
      id = this._generateMessageId(args.method);
      this._pending.set(id, completer);

      this._pendingTimeoutId.set(
        id,
        setTimeout(() => {
          if (this._pending.has(id)) {
            console.debug(
              `provider-webworker: runOnService message.id: ${id} timeout args: %O returnType: %O`,
              args,
              returnType,
            );
          }

          this._pendingTimeoutId.delete(id);
        }, RPC_TIMEOUT),
      );

      promise.catch((err) => {
        logger.out.info(err);
        completer.reject(err);
      });

      promise = promise.then((value: any) => {
        if (this._serializer == null) {
          return value;
        } else {
          return this._serializer.deserialize(value, returnType);
        }
      });
    } else {
      promise = null;
    }

    // TODO(jteplitz602): Create a class for these messages so we don't keep using StringMap #3685
    const message = { method: args.method, args: fnArgs };
    if (id != null) {
      (message as any) /** TODO #9100 */["id"] = id;
    }
    if (this._hasTransfers) {
      this._sink.emit({ data: message, transfer: transfer });
    } else {
      this._sink.emit({ data: message });
    }

    return promise;
  }

  private _handleMessage(message: ResponseMessageData): void {
    if (message.type === "result" || message.type === "error") {
      const id = message.id!;
      if (this._pending.has(id)) {
        if (message.type === "result") {
          this._pending.get(id)!.resolve(message.value);
        } else {
          this._pending.get(id)!.reject(message.value);
        }
        this._pending.delete(id);
        clearTimeout(this._pendingTimeoutId.get(id));
        this._pendingTimeoutId.delete(id);
      } else {
        console.debug(`clientBroker._handleMessage id not pending ${id}`);
      }
    }
  }
}

interface RequestMessageData {
  method: string;
  args?: any[];
  id?: string;
}

interface ResponseMessageData {
  type: "result" | "error";
  value?: any;
  id?: string;
}

/**
 * @experimental WebWorker support in Angular is experimental.
 */
export class FnArg {
  // no trust for instanceof with ts yet -dc
  readonly isTransferArg = false;

  constructor(public value: any /** TODO #9100 */, public type: any) {}
}

export class FnTransferableArg {
  // no trust for instanceof with ts yet -dc
  // here argument must also be explicitly included in the transfer list
  readonly isTransferArg = true;

  constructor(public value: ArrayBuffer | Blob | MessagePort, public type: any) {}
}

/**
 * @experimental WebWorker support in Angular is experimental.
 */
export class UiArguments {
  constructor(public method: string, public args?: Array<FnArg | FnTransferableArg>) {}
}
